import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Home, ChevronRight, Trophy, Target, Lightbulb, Clock,
  Pencil, CheckCircle, XCircle, RefreshCw, Award, Zap,
  BookOpen, Brain, Star, Flame, ArrowLeft, Send, Sparkles,
  Calculator, FlaskConical, BookText, Shapes, Lock, Bot,
  Calendar, History, Users, Cpu, PenTool, Eraser, Undo, Trash2,
  Atom, Sigma, Dna
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Subject icons mapping
const SUBJECT_ICONS = {
  maths: Calculator,
  'further-maths': Sigma,
  physics: Atom,
  english: BookText,
  chemistry: FlaskConical,
  biology: Dna,
  'verbal-reasoning': Brain,
  'non-verbal-reasoning': Shapes
};

// Tier colors
const TIER_CONFIG = {
  core: { color: 'from-green-500 to-emerald-600', bg: 'bg-green-500/10', text: 'text-green-400', icon: Star },
  stretch: { color: 'from-amber-500 to-orange-600', bg: 'bg-amber-500/10', text: 'text-amber-400', icon: Flame },
  elite: { color: 'from-purple-500 to-violet-600', bg: 'bg-purple-500/10', text: 'text-purple-400', icon: Trophy }
};

// Simple Scratch Pad Component
const ScratchPad = ({ isOpen, onClose }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#ffffff');
  const [lineWidth, setLineWidth] = useState(2);

  useEffect(() => {
    if (isOpen && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#1e293b';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  }, [isOpen]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    ctx.strokeStyle = tool === 'eraser' ? '#1e293b' : color;
    ctx.lineWidth = tool === 'eraser' ? 20 : lineWidth;
    ctx.lineCap = 'round';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="fixed bottom-4 right-4 z-50 bg-slate-800 rounded-xl border border-slate-600 shadow-2xl"
    >
      <div className="p-3 border-b border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PenTool className="h-4 w-4 text-purple-400" />
          <span className="text-white font-medium text-sm">Scratch Pad</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={tool === 'pen' ? 'default' : 'ghost'}
            onClick={() => setTool('pen')}
            className="h-7 w-7 p-0"
          >
            <PenTool className="h-3 w-3" />
          </Button>
          <Button
            size="sm"
            variant={tool === 'eraser' ? 'default' : 'ghost'}
            onClick={() => setTool('eraser')}
            className="h-7 w-7 p-0"
          >
            <Eraser className="h-3 w-3" />
          </Button>
          <div className="flex gap-1 mx-2">
            {['#ffffff', '#ef4444', '#22c55e', '#3b82f6', '#eab308'].map(c => (
              <button
                key={c}
                className={`w-5 h-5 rounded-full border-2 ${color === c ? 'border-white' : 'border-transparent'}`}
                style={{ backgroundColor: c }}
                onClick={() => setColor(c)}
              />
            ))}
          </div>
          <Button size="sm" variant="ghost" onClick={clearCanvas} className="h-7 w-7 p-0">
            <Trash2 className="h-3 w-3" />
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose} className="h-7 px-2 text-slate-400">
            ✕
          </Button>
        </div>
      </div>
      <canvas
        ref={canvasRef}
        width={400}
        height={300}
        className="rounded-b-xl cursor-crosshair"
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
      />
    </motion.div>
  );
};

// Challenge Card Component
const ChallengeCard = ({ challenge, type, onSelect, isEmpty }) => {
  if (isEmpty) {
    return (
      <Card className="bg-slate-800/30 border-slate-700 border-dashed h-full min-h-[300px] flex items-center justify-center">
        <CardContent className="text-center py-12">
          {type === 'tutor' ? (
            <>
              <Users className="h-16 w-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-400 mb-2">Tutor is busy thinking...</h3>
              <p className="text-slate-500 text-sm">Come back tomorrow for a new challenge!</p>
            </>
          ) : (
            <>
              <Cpu className="h-16 w-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-400 mb-2">Generating your challenge...</h3>
              <p className="text-slate-500 text-sm">AI is preparing a personalized problem</p>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  const tierConfig = TIER_CONFIG[challenge.tier] || TIER_CONFIG.core;
  const isAI = type === 'ai';

  return (
    <motion.div whileHover={{ scale: 1.01 }} className="h-full">
      <Card 
        className={`bg-slate-800/50 border-slate-700 h-full cursor-pointer hover:border-slate-500 transition-all ${
          challenge.is_solved ? 'ring-2 ring-green-500/30' : ''
        }`}
        onClick={() => onSelect(challenge)}
        data-testid={`challenge-card-${type}`}
      >
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between mb-2">
            <Badge className={`${isAI ? 'bg-cyan-500/20 text-cyan-400' : 'bg-purple-500/20 text-purple-400'} border-0`}>
              {isAI ? (
                <><Bot className="h-3 w-3 mr-1" /> AI Challenge</>
              ) : (
                <><Users className="h-3 w-3 mr-1" /> Tutor Challenge</>
              )}
            </Badge>
            <Badge className={`${tierConfig.bg} ${tierConfig.text} border-0`}>
              <tierConfig.icon className="h-3 w-3 mr-1" />
              {challenge.tier?.charAt(0).toUpperCase() + challenge.tier?.slice(1)}
            </Badge>
          </div>
          <CardTitle className="text-white text-lg">{challenge.title}</CardTitle>
          <CardDescription className="text-slate-400">
            {isAI ? `Generated by ${challenge.llm_used || 'AI'}` : `by ${challenge.tutor_name}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-slate-300 text-sm mb-4 line-clamp-3">{challenge.question_text}</p>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {challenge.is_solved ? (
                <Badge className="bg-green-500/20 text-green-400 border-0">
                  <CheckCircle className="h-3 w-3 mr-1" /> Solved
                </Badge>
              ) : (
                <Badge variant="outline" className="border-slate-600 text-slate-400">
                  <Target className="h-3 w-3 mr-1" /> {challenge.attempts_remaining}/3 attempts
                </Badge>
              )}
            </div>
            <ChevronRight className="h-5 w-5 text-slate-500" />
          </div>
          
          {challenge.skill_tags && (
            <div className="flex flex-wrap gap-1 mt-3">
              {challenge.skill_tags.slice(0, 3).map(tag => (
                <span key={tag} className="px-2 py-0.5 rounded-full bg-slate-700/50 text-slate-400 text-xs">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Past Challenge Item
const PastChallengeItem = ({ challenge, onSelect }) => {
  const isAI = challenge.source === 'ai';
  
  return (
    <div 
      className="flex items-center gap-4 p-4 rounded-xl bg-slate-800/30 border border-slate-700/50 hover:border-slate-600 cursor-pointer transition-all"
      onClick={() => onSelect(challenge)}
    >
      <div className={`p-2 rounded-lg ${isAI ? 'bg-cyan-500/10' : 'bg-purple-500/10'}`}>
        {isAI ? <Bot className="h-5 w-5 text-cyan-400" /> : <Users className="h-5 w-5 text-purple-400" />}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-white font-medium truncate">{challenge.title}</h4>
        <p className="text-slate-500 text-sm">
          {new Date(challenge.last_attempt_date || challenge.created_at).toLocaleDateString()}
        </p>
      </div>
      <div className="flex items-center gap-2">
        {challenge.is_solved ? (
          <Badge className="bg-green-500/20 text-green-400 border-0">
            <CheckCircle className="h-3 w-3 mr-1" /> Solved
          </Badge>
        ) : (
          <Badge className="bg-red-500/20 text-red-400 border-0">
            <XCircle className="h-3 w-3 mr-1" /> Incomplete
          </Badge>
        )}
      </div>
    </div>
  );
};

// Main Component
const EdsWhiteboardX = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('today');
  const [selectedSubject, setSelectedSubject] = useState('maths');
  const [subjects, setSubjects] = useState([]);
  const [dailyChallenges, setDailyChallenges] = useState({ tutor_challenge: null, ai_challenge: null });
  const [pastChallenges, setPastChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [attemptResult, setAttemptResult] = useState(null);
  const [showReflection, setShowReflection] = useState(false);
  const [reflection, setReflection] = useState({ hardest: '', tryDifferently: '' });
  const [aiHint, setAiHint] = useState(null);
  const [hintLlmUsed, setHintLlmUsed] = useState(null);
  const [loadingHint, setLoadingHint] = useState(false);
  const [showScratchPad, setShowScratchPad] = useState(false);
  
  const token = localStorage.getItem('token');
  
  const LLM_MODELS = [
    { id: 'openai', name: 'GPT-4o' },
    { id: 'anthropic', name: 'Claude Sonnet' },
    { id: 'gemini', name: 'Gemini Flash' }
  ];
  
  const getRandomLLM = () => LLM_MODELS[Math.floor(Math.random() * LLM_MODELS.length)].id;

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }
    fetchSubjects();
  }, [token]);

  useEffect(() => {
    if (selectedSubject) {
      fetchDailyChallenges();
      if (activeTab === 'past') {
        fetchPastChallenges();
      }
    }
  }, [selectedSubject, activeTab]);

  const fetchSubjects = async () => {
    try {
      const response = await fetch(`${API}/whiteboard/available-subjects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSubjects(data.subjects || []);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const fetchDailyChallenges = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/whiteboard/daily-challenges/${selectedSubject}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setDailyChallenges(data);
    } catch (error) {
      console.error('Error fetching daily challenges:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPastChallenges = async () => {
    try {
      const response = await fetch(`${API}/whiteboard/past-challenges?subject=${selectedSubject}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setPastChallenges(data.challenges || []);
    } catch (error) {
      console.error('Error fetching past challenges:', error);
    }
  };

  const selectChallenge = async (challenge) => {
    try {
      const response = await fetch(`${API}/whiteboard/challenges/${challenge.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSelectedChallenge(data);
      setCurrentAnswer('');
      setAttemptResult(null);
      setAiHint(null);
    } catch (error) {
      console.error('Error fetching challenge:', error);
    }
  };

  const submitAttempt = async () => {
    if (!currentAnswer.trim() || !selectedChallenge) return;
    
    const attemptNumber = (selectedChallenge.user_attempts?.length || 0) + 1;
    
    try {
      const response = await fetch(`${API}/whiteboard/attempts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          challenge_id: selectedChallenge.id,
          answer: currentAnswer,
          attempt_number: attemptNumber
        })
      });
      
      const result = await response.json();
      setAttemptResult(result);
      
      // Refresh challenge
      await selectChallenge(selectedChallenge);
      
      if (result.is_correct || attemptNumber >= 3) {
        setShowReflection(true);
      }
    } catch (error) {
      console.error('Error submitting attempt:', error);
    }
  };

  const getAIHint = async () => {
    if (!selectedChallenge) return;
    
    setLoadingHint(true);
    try {
      const randomLLM = getRandomLLM();
      const response = await fetch(`${API}/whiteboard/ai/generate-hint`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          challenge_id: selectedChallenge.id,
          question_text: selectedChallenge.question_text,
          correct_answer: selectedChallenge.correct_answer || 'unknown',
          user_answer: currentAnswer,
          attempt_number: (selectedChallenge.user_attempts?.length || 0) + 1,
          llm_provider: randomLLM
        })
      });
      
      const data = await response.json();
      if (data.hint) {
        setAiHint(data.hint);
        setHintLlmUsed(data.llm_used || LLM_MODELS.find(m => m.id === randomLLM)?.name);
      }
    } catch (error) {
      console.error('Error getting AI hint:', error);
    } finally {
      setLoadingHint(false);
    }
  };

  const submitReflection = async () => {
    if (!selectedChallenge) return;
    
    try {
      await fetch(`${API}/whiteboard/reflection`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          challenge_id: selectedChallenge.id,
          hardest_part: reflection.hardest,
          try_differently: reflection.tryDifferently
        })
      });
      
      setShowReflection(false);
      setReflection({ hardest: '', tryDifferently: '' });
      setSelectedChallenge(null);
      fetchDailyChallenges();
    } catch (error) {
      console.error('Error submitting reflection:', error);
    }
  };

  // Challenge Detail View
  if (selectedChallenge) {
    const tierConfig = TIER_CONFIG[selectedChallenge.tier] || TIER_CONFIG.core;
    const isAI = selectedChallenge.source === 'ai';
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        {/* Header */}
        <div className={`bg-gradient-to-r ${isAI ? 'from-cyan-600 via-blue-600 to-indigo-600' : 'from-violet-600 via-purple-600 to-indigo-600'} py-6 px-4 md:px-8`}>
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <Button 
                variant="ghost" 
                onClick={() => setSelectedChallenge(null)} 
                className="text-white hover:bg-white/20"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to Challenges
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => setShowScratchPad(!showScratchPad)} 
                className="text-white hover:bg-white/20"
              >
                <PenTool className="mr-2 h-4 w-4" /> Scratch Pad
              </Button>
            </div>
            
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-xl ${isAI ? 'bg-cyan-500/20' : 'bg-white/10'}`}>
                {isAI ? <Bot className="h-8 w-8 text-white" /> : <Users className="h-8 w-8 text-white" />}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Badge className={`${tierConfig.bg} ${tierConfig.text} border-0`}>
                    <tierConfig.icon className="h-3 w-3 mr-1" />
                    {selectedChallenge.tier?.charAt(0).toUpperCase() + selectedChallenge.tier?.slice(1)}
                  </Badge>
                  {isAI && selectedChallenge.llm_used && (
                    <Badge className="bg-white/20 text-white border-0">
                      {selectedChallenge.llm_used}
                    </Badge>
                  )}
                </div>
                <h1 className="text-2xl font-bold text-white">{selectedChallenge.title}</h1>
                <p className="text-white/70">{isAI ? 'AI Generated Challenge' : `by ${selectedChallenge.tutor_name}`}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-6 space-y-6">
              {/* Question */}
              <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Target className="h-5 w-5 text-purple-400" />
                  Challenge Question
                </h3>
                <p className="text-slate-200 text-lg leading-relaxed whitespace-pre-wrap">{selectedChallenge.question_text}</p>
              </div>

              {/* Attempts Tracker */}
              <div className="flex items-center gap-4">
                <span className="text-slate-400">Attempts:</span>
                {[1, 2, 3].map(num => {
                  const attempt = selectedChallenge.user_attempts?.find(a => a.attempt_number === num);
                  return (
                    <div
                      key={num}
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        attempt 
                          ? attempt.is_correct 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-red-500/20 text-red-400'
                          : 'bg-slate-700/50 text-slate-500'
                      }`}
                    >
                      {attempt ? (attempt.is_correct ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />) : num}
                    </div>
                  );
                })}
                <span className="text-slate-500 ml-2">
                  {selectedChallenge.attempts_remaining} remaining
                </span>
              </div>

              {/* Answer Input */}
              {selectedChallenge.attempts_remaining > 0 && !selectedChallenge.is_solved && (
                <div className="space-y-4">
                  <Input
                    value={currentAnswer}
                    onChange={(e) => setCurrentAnswer(e.target.value)}
                    placeholder="Enter your answer..."
                    className="bg-slate-900/50 border-slate-600 text-white text-lg"
                    data-testid="answer-input"
                  />
                  
                  <div className="flex gap-3">
                    <Button 
                      onClick={submitAttempt}
                      className={`bg-gradient-to-r ${isAI ? 'from-cyan-600 to-blue-600' : 'from-purple-600 to-indigo-600'} flex-1`}
                      disabled={!currentAnswer.trim()}
                      data-testid="submit-answer"
                    >
                      <Send className="h-4 w-4 mr-2" /> Submit Answer
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={getAIHint}
                      disabled={loadingHint}
                      className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                    >
                      {loadingHint ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                      <span className="ml-2">AI Hint</span>
                    </Button>
                  </div>
                </div>
              )}

              {/* AI Hint */}
              {aiHint && (
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-purple-400">
                      <Lightbulb className="h-5 w-5" />
                      <span className="font-semibold">AI Hint</span>
                    </div>
                    {hintLlmUsed && (
                      <Badge variant="outline" className="border-cyan-500/50 text-cyan-400 text-xs">
                        {hintLlmUsed}
                      </Badge>
                    )}
                  </div>
                  <p className="text-slate-300">{aiHint}</p>
                </div>
              )}

              {/* Result Display */}
              {attemptResult && (
                <div className={`rounded-xl p-4 ${attemptResult.is_correct ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {attemptResult.is_correct ? (
                      <CheckCircle className="h-6 w-6 text-green-400" />
                    ) : (
                      <XCircle className="h-6 w-6 text-red-400" />
                    )}
                    <span className={`font-semibold ${attemptResult.is_correct ? 'text-green-400' : 'text-red-400'}`}>
                      {attemptResult.message}
                    </span>
                  </div>
                  {attemptResult.hint && <p className="text-slate-300 mt-2">{attemptResult.hint}</p>}
                  {attemptResult.correct_answer && (
                    <p className="text-slate-300 mt-2">
                      <span className="text-slate-400">Correct answer: </span>
                      <span className="font-semibold">{attemptResult.correct_answer}</span>
                    </p>
                  )}
                </div>
              )}

              {/* Solved State */}
              {selectedChallenge.is_solved && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 text-center">
                  <Award className="h-12 w-12 text-green-400 mx-auto mb-3" />
                  <h3 className="text-xl font-bold text-green-400 mb-2">Challenge Complete!</h3>
                  <p className="text-slate-300">Great job solving this challenge.</p>
                </div>
              )}

              {/* Reflection */}
              {showReflection && (
                <div className="bg-slate-900/80 border border-slate-600 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Brain className="h-5 w-5 text-purple-400" />
                    Reflection Time
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-slate-400 text-sm block mb-2">What did you find hardest?</label>
                      <Textarea
                        value={reflection.hardest}
                        onChange={(e) => setReflection(r => ({ ...r, hardest: e.target.value }))}
                        placeholder="Describe the challenging part..."
                        className="bg-slate-800 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <label className="text-slate-400 text-sm block mb-2">What would you try differently?</label>
                      <Textarea
                        value={reflection.tryDifferently}
                        onChange={(e) => setReflection(r => ({ ...r, tryDifferently: e.target.value }))}
                        placeholder="Share your thoughts..."
                        className="bg-slate-800 border-slate-600 text-white"
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button onClick={submitReflection} className="bg-purple-600 flex-1">
                        Submit Reflection
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => { setShowReflection(false); setSelectedChallenge(null); }}
                        className="border-slate-600 text-slate-400"
                      >
                        Skip
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Scratch Pad */}
        <ScratchPad isOpen={showScratchPad} onClose={() => setShowScratchPad(false)} />
      </div>
    );
  }

  // Main View
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 py-6 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate(-1)} 
              className="text-white hover:bg-white/20"
              data-testid="back-btn"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => navigate('/categories')} 
              className="text-white hover:bg-white/20"
            >
              <Home className="mr-2 h-4 w-4" /> Home
            </Button>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/10 rounded-xl">
              <Pencil className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">Eds Whiteboard X</h1>
              <p className="text-purple-200">Daily Challenges from Tutors & AI</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        <div className="flex gap-6">
          {/* Left Sidebar - Subjects */}
          <div className="w-64 flex-shrink-0 hidden lg:block">
            <Card className="bg-slate-800/50 border-slate-700 sticky top-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-purple-400" />
                  Subjects
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {subjects.map(subject => {
                  const Icon = SUBJECT_ICONS[subject.id] || Calculator;
                  const isActive = selectedSubject === subject.id;
                  const isDisabled = !subject.enabled;
                  
                  return (
                    <button
                      key={subject.id}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${
                        isDisabled 
                          ? 'opacity-50 cursor-not-allowed bg-slate-800/30' 
                          : isActive
                            ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                            : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
                      }`}
                      onClick={() => !isDisabled && setSelectedSubject(subject.id)}
                      disabled={isDisabled}
                      data-testid={`subject-${subject.id}`}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="flex-1 text-left">{subject.name}</span>
                      {isDisabled && (
                        <Badge className="bg-slate-700 text-slate-400 text-[10px]">
                          Soon
                        </Badge>
                      )}
                    </button>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Mobile Subject Selector */}
            <div className="lg:hidden mb-4">
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="w-full p-3 rounded-lg bg-slate-800 border border-slate-700 text-white"
              >
                {subjects.filter(s => s.enabled).map(subject => (
                  <option key={subject.id} value={subject.id}>{subject.name}</option>
                ))}
              </select>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="bg-slate-800/50 border border-slate-700 p-1 mb-6">
                <TabsTrigger 
                  value="today" 
                  className="data-[state=active]:bg-purple-600 data-[state=active]:text-white"
                  data-testid="tab-today"
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Today's Challenges
                </TabsTrigger>
                <TabsTrigger 
                  value="past" 
                  className="data-[state=active]:bg-purple-600 data-[state=active]:text-white"
                  data-testid="tab-past"
                >
                  <History className="h-4 w-4 mr-2" />
                  Past Challenges
                </TabsTrigger>
              </TabsList>

              {/* Today's Challenges */}
              <TabsContent value="today">
                {loading ? (
                  <div className="flex items-center justify-center py-20">
                    <RefreshCw className="h-8 w-8 text-purple-400 animate-spin" />
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Tutor Challenge */}
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <Users className="h-5 w-5 text-purple-400" />
                        <h2 className="text-lg font-semibold text-white">Tutor's Challenge</h2>
                      </div>
                      <ChallengeCard
                        challenge={dailyChallenges.tutor_challenge}
                        type="tutor"
                        onSelect={selectChallenge}
                        isEmpty={!dailyChallenges.tutor_challenge}
                      />
                    </div>

                    {/* AI Challenge */}
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <Bot className="h-5 w-5 text-cyan-400" />
                        <h2 className="text-lg font-semibold text-white">AI Challenge</h2>
                      </div>
                      <ChallengeCard
                        challenge={dailyChallenges.ai_challenge}
                        type="ai"
                        onSelect={selectChallenge}
                        isEmpty={!dailyChallenges.ai_challenge}
                      />
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Past Challenges */}
              <TabsContent value="past">
                {pastChallenges.length === 0 ? (
                  <div className="text-center py-20">
                    <History className="h-16 w-16 text-slate-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-slate-400 mb-2">No Past Challenges</h3>
                    <p className="text-slate-500">Complete today's challenges to see them here!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pastChallenges.map(challenge => (
                      <PastChallengeItem 
                        key={challenge.id} 
                        challenge={challenge} 
                        onSelect={selectChallenge}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EdsWhiteboardX;
